import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import styles from '../../Games/components/ShowPartners.module.scss';
import { useStore } from '../../../../common';
import { AuthContext } from '../../../../logic';
import { Button } from 'esa-ui-library';
import { PERMISSIONS } from '../../../constants';
import { getPartnerGameSettings } from '../../../../api';
import { CSVLink } from 'react-csv';
import { format } from 'date-fns';

interface ShowGamesProps {
	onPrefsClick: (partner: { id: number; name: string; partner: string }) => void;
	onSettingsClick: (partner: { id: number; name: string; partner: string; gameId: number }) => void;
}

const ShowGames: FC<ShowGamesProps> = ({ onPrefsClick, onSettingsClick }): JSX.Element => {
	const [state, dispatch] = useStore();
	const { selectedGamePartner, partnerGames, partnerGamesLoading } = state;
	const { token, user } = useContext(AuthContext);
	const [isExporting, setIsExporting] = useState<boolean>(false);
	const [csvData, setCsvData] = useState<any[]>([]);
	const [csvFilename, setCsvFilename] = useState<string>('');
	const csvInstance = useRef<HTMLDivElement | null>(null);

	const getGames = async () => {
		if (selectedGamePartner) {
			dispatch('PARTNER_GAMES_LOADING', true);
			await dispatch('FETCH_PARTNER_GAMES', {
				token,
				partnerId: selectedGamePartner.skinid,
			});
		}
	};

	useEffect(() => {
		getGames();
	}, [selectedGamePartner]);

	const handleDelete = async (gameId: number) => {
		if (!selectedGamePartner) return;
		if (!window.confirm('Are you sure you want to remove this game?')) return;

		await dispatch('REMOVE_GAME', {
			token,
			gameId,
			partnerId: selectedGamePartner.skinid,
		});

		await getGames();
	};

	const handleExportSettings = async () => {
		if (!selectedGamePartner) return;
		setIsExporting(true);
		try {
			const res = await getPartnerGameSettings({
				token,
				partner_id: selectedGamePartner.skinid,
				game_id: 0,
			});

			if (res?.data?.resultCode === 0 || res?.data?.resultcode === 0 || res?.resultCode === 0) {
				const list = Array.isArray(res?.data?.return)
					? res.data.return
					: Array.isArray(res?.data?.data)
					? res.data.data
					: Array.isArray(res?.data)
					? res.data
					: [];

				if (list.length > 0) {
					const partnerParts = Array.from(
						new Set([selectedGamePartner.partner_name, selectedGamePartner.name].filter(Boolean))
					)
						.join('_')
						.toLowerCase()
						.replace(/\s+/g, '_');
					const partnerName =
						partnerParts || selectedGamePartner.partnerid?.toLowerCase().replace(/\s+/g, '_') || 'partner';
					const dateStr = format(new Date(), 'yyyy-MM-dd');
					const filename = `${partnerName}-games-settings-${dateStr}.csv`;

					setCsvData(list);
					setCsvFilename(filename);

					setTimeout(() => {
						csvInstance.current?.click();
					}, 0);
				} else {
					dispatch('GLOBAL_ERROR', ['No game settings found']);
				}
			} else {
				const errorMsg = res?.data?.errorMessage || res?.data?.message || 'Failed to retrieve game settings';
				dispatch('GLOBAL_ERROR', [errorMsg]);
			}
		} catch (error: any) {
			dispatch('GLOBAL_ERROR', [error?.message || 'Failed to retrieve game settings']);
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<section className={styles.ShowPartners}>
			<div className={styles.ShowProviders}>
				<div className={styles.Header}>
					<h2>Assigned Games</h2>
					<Button small primary onClick={handleExportSettings} disabled={isExporting || !selectedGamePartner}>
						{isExporting ? 'Exporting...' : 'Export Game Settings'}
					</Button>
				</div>
				<CSVLink data={csvData} filename={csvFilename} className={styles.ButtonCSV}>
					<div ref={csvInstance} />
				</CSVLink>
				<div className={styles.TableScroll}>
					<table>
						<thead>
							<tr>
								<th>Internal Game ID</th>
								<th>Name</th>
								<th>Provider</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{!partnerGamesLoading &&
								Array.isArray(partnerGames) &&
								partnerGames.map((game: any) => (
									<tr key={game.game_id || game.internal_game_id}>
										<td>{game.internal_game_id}</td>
										<td>{game.game_name}</td>
										<td>{game.game_provider_name}</td>
										<td>
											<div className={styles.ActionButtons}>
												{(user?.permissions?.isSuperAdmin || user?.permissions[PERMISSIONS.canGetSkinPrefs]) && (
													<Button
														small
														className={styles.PrefsButton}
														onClick={() => {
															if (!selectedGamePartner) return;
															onPrefsClick({
																id: selectedGamePartner.skinid,
																name: selectedGamePartner.partner_name,
																partner: selectedGamePartner.partnerid,
															});
														}}
													>
														Prefs
													</Button>
												)}
												<Button
													small
													className={styles.SettingsButton}
													onClick={() => {
														if (!selectedGamePartner) return;
														onSettingsClick({
															id: selectedGamePartner.skinid,
															name: selectedGamePartner.partner_name,
															partner: selectedGamePartner.partnerid,
															gameId: game.internal_game_id,
														});
													}}
												>
													Game Settings
												</Button>
												<Button
													small
													danger
													className={styles.DeleteButton}
													onClick={() => handleDelete(game.internal_game_id)}
												>
													Delete
												</Button>
											</div>
										</td>
									</tr>
								))}
							{partnerGamesLoading && (
								<tr>
									<td colSpan={4}>Loading...</td>
								</tr>
							)}
							{!partnerGamesLoading && (!partnerGames || partnerGames.length === 0) && (
								<tr>
									<td colSpan={4}>No games assigned</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</div>
		</section>
	);
};

export { ShowGames };
